import { Button, Flex, Tooltip, Typography } from 'antd';
import { LuFilter } from 'react-icons/lu';
import { useCollectionStore, selectNodeChecked } from '@stores/useCollectionStore';
import { useProject } from '@contexts/Project.context';
import {
	ACTIVE_FILTER_STYLE,
	COLLECTION_SIDEBAR_IDS,
	FALLBACK_OBJECT_COLOR,
	HIDDEN_ROW_OPACITY,
} from '../constants';
import DetectionPointerPicker from './DetectionPointerPicker';
import ObjectColorPicker from './ObjectColorPicker';

const { Text } = Typography;

/** Why a *visible* row reads 0; hidden rows never get here. */
const zeroCountTitle = count => (count ? `All ${count} filtered out` : 'Not detected in this run');

/** The button is always rendered, so it has to say why it is disabled. */
const filterActionTitle = (label, { hasFilters, isChecked, activeCount }) => {
	if (!hasFilters) return `Nothing to filter for ${label}`;
	if (!isChecked) return `Show ${label} to filter it`;
	return activeCount ? `Filter ${label} (${activeCount} active)` : `Filter ${label}`;
};

/** Without this the tree reads a click on a row control as a click on the row. */
const RowAction = ({ children }) => (
	<span role="presentation" onClick={e => e.stopPropagation()}>
		{children}
	</span>
);

/**
 * Renders the title content for a single node in the antd Tree.
 * Used via `titleRender` — receives node data as a prop.
 *
 * Uses a granular per-node selector (returns a boolean) so only this
 * node re-renders when its own checked state changes.
 *
 * @param {object} props
 * @param {object} props.node - node data
 * @param {(key: string, pointer: string) => void} props.onPointerChange
 * @param {(key: string, hex: string, opacity: number) => void} [props.onColorPreview]
 * @param {(key: string, hex: string, opacity: number) => void} [props.onColorCommit]
 * @param {() => void} [props.onColorCancel]
 */
const CollectionNode = ({
	node,
	onPointerChange,
	onColorPreview,
	onColorCommit,
	onColorCancel,
}) => {
	const isGroup = !!node.children?.length;
	const isChecked = useCollectionStore(selectNodeChecked(node.key));
	const setSelectedObjectId = useCollectionStore(s => s.setSelectedObjectId);
	const { setActiveSidebar } = useProject();
	// Already hex — `useCollectionData` normalizes it at the data boundary. The
	// fallback matters: with styling gone from the object panel this swatch is
	// the only color control, so an object whose stored color was absent or
	// unparseable would otherwise render red on the map with no way to fix it.
	const color = node.color ?? FALLBACK_OBJECT_COLOR;
	// The panel behind this icon is filters and nothing else now, so an object
	// with no filterable properties has nothing to open.
	const hasFilters = (node.filters?.length ?? 0) > 0;
	// Counted upstream against effective configs so it matches the panel this
	// button opens — the row can't see the collection ceiling.
	const activeFilterCount = node.activeFilterCount ?? 0;
	const isHiddenLeaf = !isGroup && !isChecked;
	const canFilter = isChecked && hasFilters;

	// Derive live filtered count directly from filteredFeatures so this node
	// re-renders whenever the filtered set changes (toggle, slider, apply).
	// Group nodes show the collection total; leaf nodes show the per-object count.
	const liveCount = useCollectionStore(s =>
		isGroup ? s.filteredFeatures.length : (s.filteredCountByKey[node.key] ?? 0)
	);

	const countTitle =
		!isGroup && isChecked && liveCount === 0 ? zeroCountTitle(node.count) : undefined;

	const filterTitle = filterActionTitle(node.label, {
		hasFilters,
		isChecked,
		activeCount: activeFilterCount,
	});

	const handleFilterClick = () => {
		setSelectedObjectId(node.key);
		setActiveSidebar({
			sidebarId: COLLECTION_SIDEBAR_IDS.objectFilter,
			parentId: 'layerView',
		});
	};

	// Hover reaches neither a keyboard nor a screen reader, so the explanation
	// doubles as the accessible name. `role="img"` is what lets a plain span
	// take one.
	const count = (
		<Text
			type="secondary"
			role={countTitle ? 'img' : undefined}
			aria-label={countTitle}
			style={{
				fontSize: '0.75rem',
				textAlign: 'right',
				fontVariantNumeric: 'tabular-nums',
				flexShrink: 0,
			}}>
			{isGroup ? `${liveCount} objects` : liveCount}
		</Text>
	);

	return (
		// Full-width row: swatch + pointer read as a legend, the name takes the
		// slack, count and actions share a right-hand column. Depends on
		// CollectionTree's blockNode.
		<Flex
			align="center"
			gap={8}
			style={{ width: '100%', opacity: isHiddenLeaf ? HIDDEN_ROW_OPACITY : 1 }}>
			{!isGroup && (
				<RowAction>
					<ObjectColorPicker
						objectKey={node.key}
						label={node.label}
						color={color}
						opacity={node.opacity}
						onPreview={onColorPreview}
						onCommit={onColorCommit}
						onCancel={onColorCancel}
					/>
				</RowAction>
			)}

			{!isGroup && onPointerChange && (
				<RowAction>
					<DetectionPointerPicker
						objectKey={node.key}
						label={node.label}
						pointer={node.pointer}
						defaultPointer={node.defaultPointer}
						availability={node.availability}
						color={color}
						onChange={onPointerChange}
					/>
				</RowAction>
			)}

			<Text
				ellipsis={{ tooltip: node.label }}
				style={{
					flex: 1,
					minWidth: 0,
					fontSize: isGroup ? '0.7rem' : '0.875rem',
					textTransform: isGroup ? 'uppercase' : 'none',
					fontWeight: isGroup ? 600 : 400,
					letterSpacing: isGroup ? '0.06em' : undefined,
				}}
				type={isGroup ? 'secondary' : undefined}>
				{node.label}
			</Text>

			{/* A hidden row has no count in this column's metric — its only
			    available number is the unfiltered total, so it shows none. */}
			{node.count != null &&
				!isHiddenLeaf &&
				(countTitle ? <Tooltip title={countTitle}>{count}</Tooltip> : count)}

			{/* Always rendered — dropping it collapses the row's action column. */}
			{!isGroup && (
				<RowAction>
					<Tooltip title={filterTitle}>
						<Button
							type="text"
							size="small"
							icon={<LuFilter />}
							disabled={!canFilter}
							onClick={handleFilterClick}
							aria-label={filterTitle}
							style={
								canFilter && activeFilterCount > 0 ? ACTIVE_FILTER_STYLE : undefined
							}
						/>
					</Tooltip>
				</RowAction>
			)}
		</Flex>
	);
};

export default CollectionNode;
